// The card list that replaces the per-circuit table in CableTakeoffTabV3. One
// CircuitCard per BoQ item, grouped by the board it feeds from and ordered by
// the id each row prints — see `sortCableRows`.

import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { ChevronRight } from "lucide-react";
import type { CableBoqItem } from "../../utils/fetchCables";
import type { EditableCableTakeoff } from "../useEditableCableTakeoff";
import type { CableDestinationInfo } from "../useCableDestinationInfo";
import type { RouteStatusKind } from "../karlRoutes";
import type { CircuitKeyMap } from "../circuitKeys";
import type { CircuitBreakerMap } from "../circuitBreakers";
import type { KarlDiagramControl } from "../CablesChatPanel/types";
import { CircuitCard, type FeedPointOption } from "./CircuitCard";
import type { CircuitOption } from "./types";
import { sortCableRows } from "../sortCableRows";
import type { AutoSplitPlan } from "../splitByRooms";
import { lumpedCircuitLimit } from "../lumpedCircuitLimit";

const UNFED = "";
// Not a board label: a marker label is never this, so the feeders cannot collide with a real board.
const FEEDERS = "\u0000feeders";

export interface CircuitCardListProps {
  items: CableBoqItem[];
  /** Locale-aware metre formatting, so a German reads 6.006,5 not 6,006.5. */
  formatMetres?: (value: number) => string;
  selectedCircuit?: string | null;
  /** The run Karl is asking about right now, marked apart from a selection. */
  underReviewCircuit?: string | null;
  /** True while Karl is waiting to be told which run some devices belong on. */
  isPickingRun?: boolean;
  onPickRun?: (circuitId: string) => void;
  /** Puts a run to Karl in the thread. */
  onAskKarlAbout?: (circuitId: string) => void;
  onSplit?: (circuitId: string) => void;
  onAutoSplit?: (circuitId: string, plan: AutoSplitPlan) => void;
  planAutoSplit?: (circuitId: string, maxPerCircuit: number, takeSwitches: boolean) => AutoSplitPlan | null;
  /** Where an opened run shows its details; null keeps them under the row. */
  detailHost?: HTMLElement | null;
  onSelectCircuit?: (circuitId: string | null) => void;
  onHoverCircuit?: (circuitId: string | null) => void;
  edit?: EditableCableTakeoff;
  destinationInfo?: Record<string, CableDestinationInfo[]>;
  onHighlightDestination?: (pos: { x: number; y: number } | null) => void;
  onShowHeightMarks?: (marks: { x: number; y: number; kind: "rise" | "drop" }[]) => void;
  /** What the estimator settled per circuit while walking the routes with Karl. */
  routeStatus?: Record<string, RouteStatusKind>;
  /** Boards available on this plan, offered as each row's feed-point list. */
  feedPointOptions?: FeedPointOption[];
  /** Cable types already used in this project, offered as suggestions. */
  cableTypeOptions?: string[];
  /** The printed id per circuit, from `buildCircuitKeyMap`. Rows the map does
   * not mention print no id column at all. */
  circuitKeys?: CircuitKeyMap;
  /** The schematic's breaker designator per circuit, from
   * `buildCircuitBreakerMap` — only the runs whose schedule named one. */
  circuitBreakers?: CircuitBreakerMap;
  /** Runs the estimator created, so only those offer to be deleted. */
  addedCircuitIds?: string[];
  onRemoveCircuit?: (circuitId: string, toCircuitId: string | null) => void;
  /** The one board whose runs are shown; the others stay collapsed under their headers. */
  openBoard?: string | null;
  onOpenBoard?: (board: string | null) => void;
  /** Every board open at once, e.g. while he is searching across them. */
  isEveryBoardOpen?: boolean;
  diagram?: KarlDiagramControl;
}

const normalizeKey = (key: string): string => key.toUpperCase().replace(/[^A-Z0-9]/g, "");

export function CircuitCardList({
  items,
  formatMetres,
  selectedCircuit,
  underReviewCircuit,
  isPickingRun,
  onPickRun,
  onAskKarlAbout,
  onSplit,
  onAutoSplit,
  planAutoSplit,
  detailHost,
  onSelectCircuit,
  onHoverCircuit,
  edit,
  destinationInfo,
  onHighlightDestination,
  onShowHeightMarks,
  routeStatus,
  feedPointOptions,
  cableTypeOptions,
  circuitKeys,
  circuitBreakers,
  addedCircuitIds,
  onRemoveCircuit,
  openBoard: controlledOpenBoard,
  onOpenBoard,
  isEveryBoardOpen = false,
  diagram,
}: CircuitCardListProps) {
  const { t } = useTranslation();
  const [ownOpenBoard, setOwnOpenBoard] = useState<string | null>(null);
  const openBoard = onOpenBoard ? (controlledOpenBoard ?? null) : ownOpenBoard;
  const setOpenBoard = onOpenBoard ?? setOwnOpenBoard;
  // The order he reads: A32, A45, A121. Sorted once, above both the rows and the
  // "Move to route" menu they open — two orders for one list of runs is two
  // places to hunt.
  const ordered = useMemo(
    () => sortCableRows({ items, circuitKeys, circuitBreakers }),
    [items, circuitKeys, circuitBreakers],
  );
  // Circuits a device can be moved into (real circuit ids only), reused by every
  // card's "Move to route" menu.
  const circuitOptions = useMemo<CircuitOption[]>(
    () =>
      ordered
        .filter((it) => !!it.circuit)
        .map((it) => {
          const cid = it.circuit as string;
          const rgb = edit?.derivedGeometry?.byCircuit[cid]?.paths[0]?.rgb;
          return {
            id: cid,
            label: it.text,
            color: rgb ? `rgb(${rgb[0]},${rgb[1]},${rgb[2]})` : undefined,
          };
        }),
    [ordered, edit?.derivedGeometry],
  );

  // One section per Verteiler: the same A66 on UV1 and on UV2 are two runs, and
  // they sit under two headers. The busiest board leads; runs fed from nowhere close the list.
  const sections = useMemo(() => {
    const buckets = new Map<string, CableBoqItem[]>();
    ordered.forEach((item) => {
      const board =
        item.circuit && edit?.feederCircuits?.[item.circuit]
          ? FEEDERS
          : (item.circuit && edit?.feedingBoardByCircuit?.[item.circuit]) || UNFED;
      const arr = buckets.get(board) ?? [];
      arr.push(item);
      buckets.set(board, arr);
    });
    return [...buckets.entries()]
      .map(([board, entries]) => ({ board, entries }))
      .sort((a, b) => {
        // The cables that feed the boards come before the boards' own runs.
        if ((a.board === FEEDERS) !== (b.board === FEEDERS))
          return a.board === FEEDERS ? -1 : 1;
        if ((a.board === UNFED) !== (b.board === UNFED))
          return a.board === UNFED ? 1 : -1;
        return (
          b.entries.length - a.entries.length || a.board.localeCompare(b.board)
        );
      });
  }, [ordered, edit?.feedingBoardByCircuit, edit?.feederCircuits]);

  const boardName = (label: string): string =>
    label === UNFED
      ? t("smartSurveyor.cablesBeta.runsNotFed")
      : label === FEEDERS
      ? t("smartSurveyor.cablesBeta.feedersGroupTitle")
      : (feedPointOptions?.find((option) => option.label === label)?.name ??
        label);

  const showSectionHeaders = sections.length > 1;

  // A run picked on the plan has to be on screen, so its board opens with it.
  const selectedBoard = selectedCircuit
    ? edit?.feederCircuits?.[selectedCircuit]
      ? FEEDERS
      : (edit?.feedingBoardByCircuit?.[selectedCircuit] ?? null)
    : null;
  useEffect(() => {
    if (selectedBoard && showSectionHeaders) setOpenBoard(selectedBoard);
  }, [selectedBoard, showSectionHeaders, setOpenBoard]);

  // Only a run HE made offers to be deleted. Striking one the estimation
  // published would be a removal with nothing to put it back.
  const created = useMemo(
    () => new Set(addedCircuitIds ?? []),
    [addedCircuitIds],
  );

  return (
    <ul className="divide-y divide-gray-100 pb-2">
      {sections.map(({ board, entries }) => {
        const isOpen =
          !showSectionHeaders || isEveryBoardOpen || openBoard === board;
        const deviceCountOf = (circuitId: string | undefined): number =>
          circuitId ? (edit?.derivedGeometry?.byCircuit[circuitId]?.paths.length ?? 0) : 0;
        // Visible while the group is closed: a lumped run inside it is otherwise found only by opening every board.
        const lumpedCount = entries.filter(
          (entry) =>
            deviceCountOf(entry.circuit) >
            lumpedCircuitLimit({ category: entry.circuit ? edit?.categoryByCircuit?.[entry.circuit] : null }),
        ).length;
        return (
          <li key={board || "unfed"} className="list-none">
            {showSectionHeaders && (
              <button
                type="button"
                data-testid="runs-board-header"
                aria-expanded={isOpen}
                disabled={isEveryBoardOpen}
                onClick={() => setOpenBoard(openBoard === board ? null : board)}
                className={clsx(
                  "flex w-full items-center gap-1.5 px-2 py-2.5 text-left hover:bg-slate-50",
                  isOpen && !isEveryBoardOpen && "bg-amber-50/70",
                )}
              >
                <ChevronRight
                  size={14}
                  className={clsx(
                    "flex-none text-slate-500 transition-transform",
                    isOpen && "rotate-90",
                  )}
                />
                <span className="h-2 w-2 shrink-0 rounded-full bg-amber-500" />
                <span className="text-[13px] font-semibold uppercase tracking-wide text-slate-700">
                  {boardName(board)}
                </span>
                <span className="text-[12px] tabular-nums text-slate-500">
                  {t("smartSurveyor.cablesBeta.runsBoardCounts", {
                    count: entries.length,
                    circuits: entries.length,
                    devices: entries.reduce((sum, entry) => sum + deviceCountOf(entry.circuit), 0),
                  })}
                </span>
                {lumpedCount > 0 && (
                  <span
                    data-testid="runs-board-lumped"
                    className="ml-auto flex-none rounded bg-amber-100 px-1.5 py-0.5 text-[12px] font-medium tabular-nums text-amber-900"
                  >
                    {t("smartSurveyor.cablesBeta.runsBoardLumped", { count: lumpedCount })}
                  </span>
                )}
              </button>
            )}
            {isOpen && (
              <ul className="divide-y divide-gray-100">
                {entries.map((it, index) => {
                  const cid = it.circuit || it.text;
                  const isSel = !!selectedCircuit && cid === selectedCircuit;
                  const dests =
                    (it.circuit && destinationInfo?.[it.circuit]) || [];
                  return (
                    <CircuitCard
                      key={it.circuit || it.text || index}
                      item={it}
                      selected={isSel}
                      underReview={
                        !!underReviewCircuit && cid === underReviewCircuit
                      }
                      onSelect={onSelectCircuit}
                      onHover={onHoverCircuit}
                      edit={edit}
                      destinations={dests}
                      circuitOptions={circuitOptions}
                      onHighlightDestination={onHighlightDestination}
                      onShowHeightMarks={onShowHeightMarks}
                      status={routeStatus?.[cid]}
                      isPickingRun={isPickingRun}
                      onPickRun={onPickRun}
                      onAskKarlAbout={onAskKarlAbout}
                      onSplit={onSplit}
                      onAutoSplit={onAutoSplit}
                      planAutoSplit={planAutoSplit}
                      detailHost={detailHost}
                      formatMetres={formatMetres}
                      feedPointOptions={feedPointOptions}
                      cableTypeOptions={cableTypeOptions}
                      // Keyed by the real circuit id, never the text fallback:
                      // the map is Karl's, and Karl has no row without one.
                      circuitKey={
                        it.circuit ? circuitKeys?.[it.circuit] : undefined
                      }
                      circuitBreaker={
                        it.circuit ? circuitBreakers?.[it.circuit] : undefined
                      }
                      isCreatedByYou={!!it.circuit && !!created.has(it.circuit)}
                      onRemoveCircuit={onRemoveCircuit}
                      diagram={diagram}
                      isKeyTaken={(key) => {
                        const wanted = normalizeKey(key);
                        return Object.entries(circuitKeys ?? {}).some(
                          ([id, taken]) => id !== it.circuit && Boolean(taken) && normalizeKey(taken ?? "") === wanted,
                        );
                      }}
                    />
                  );
                })}
              </ul>
            )}
          </li>
        );
      })}
    </ul>
  );
}
